import { useEffect, useCallback } from 'react';
import { usePerformanceStore } from '../store/performanceStore';

/**
 * Hook for tracking page load performance
 * Automatically measures and records page load time
 */
export function usePageLoadTracking(pageName: string) {
  const recordPageLoad = usePerformanceStore((state) => state.recordPageLoad);

  useEffect(() => {
    const startTime = performance.now();
    
    // Wait for the page to be fully loaded
    const recordLoad = () => {
      const loadTime = performance.now() - startTime;
      recordPageLoad(pageName, loadTime, {
        navigationTiming: performance.getEntriesByType('navigation')[0],
      });
    };

    // Record on component mount completion
    if (document.readyState === 'complete') {
      recordLoad();
    } else {
      window.addEventListener('load', recordLoad);
      return () => window.removeEventListener('load', recordLoad);
    }
  }, [pageName, recordPageLoad]);
}

/**
 * Hook for tracking crashes and errors
 */
export function useErrorTracking() {
  const recordCrash = usePerformanceStore((state) => state.recordCrash);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      recordCrash(
        event.message,
        event.error?.stack,
        undefined,
        'high'
      );
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      recordCrash(
        `Unhandled Promise Rejection: ${event.reason}`,
        event.reason?.stack,
        undefined,
        'high'
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [recordCrash]);

  return useCallback(
    (error: Error, component?: string, severity: 'low' | 'medium' | 'high' | 'critical' = 'medium') => {
      recordCrash(error.message, error.stack, component, severity);
    },
    [recordCrash]
  );
}
